import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, CheckCircle2, ArrowLeft, ArrowRight, Send, Check } from "lucide-react";
import HexBackground from "@/components/HexBackground";
import { api } from "@/lib/api";

const INITIAL = {
  fio: "",
  is_budget: null, // true | false
  course: "",
  student_id: "",
  union_id: "",
  vk_link: "",
  phone: "",
  email: "",
  telegram_link: "",
  achievement_categories: [], // keys from ACHIEVEMENT_CATEGORIES chosen by the user
  science_achievements: "",
  study_achievements: "",
  social_achievements: "",
  sport_achievements: "",
  culture_achievements: "",
  drive_link: "",
  comments: "",
};

const COURSES = ["1", "2", "3", "4", "5", "6", "Аспирантура"];

// Draft is auto-saved here so a refresh, closed tab, or iOS backgrounding
// the page doesn't wipe what the person already typed.
const STORAGE_KEY = "camp_form_draft_v1";

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function saveDraft(data, step) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, step }));
  } catch {
    // localStorage can be unavailable (private mode, quota) — safe to ignore
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

const ACHIEVEMENT_CATEGORIES = [
  {
    key: "science",
    field: "science_achievements",
    label: "Научная деятельность",
    help: "Например, опубликованные статьи с указанием рейтинга журнала и ссылкой на ИСТИНУ, тезисы, участие в конференциях.",
  },
  {
    key: "study",
    field: "study_achievements",
    label: "Учебная деятельность",
    help: "Например, систематическая отличная учёба, пост старосты группы/курса.",
  },
  {
    key: "social",
    field: "social_achievements",
    label: "Общественная деятельность",
    help: "Например, должность организатора/волонтёра на университетском/факультетском мероприятии.",
  },
  {
    key: "sport",
    field: "sport_achievements",
    label: "Спортивная деятельность",
    help: "Например, победа/призёрство (с указанием места) во всероссийских, межвузовских, университетских соревнованиях.",
  },
  {
    key: "culture",
    field: "culture_achievements",
    label: "Культурно-массовая деятельность",
    help: "Например, должность режиссёра, актёра на университетском/факультетском мероприятии.",
  },
];

function Label({ required, children }) {
  return (
    <label className="field-label">
      {required && <span className="req">*</span>}
      {children}
    </label>
  );
}

function Input({ testid, value, onChange, placeholder, type = "text", inputMode }) {
  return (
    <input
      data-testid={testid}
      type={type}
      inputMode={inputMode}
      className="text-input"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function TextArea({ testid, value, onChange, placeholder }) {
  return (
    <textarea
      data-testid={testid}
      className="text-area"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function Radio({ label, checked, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`radio-row ${checked ? "selected" : ""}`}
      role="radio"
      aria-checked={checked}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 999,
          border: "2px solid #7a1d2e",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {checked && <div style={{ width: 10, height: 10, borderRadius: 999, background: "#7a1d2e" }} />}
      </div>
      <span>{label}</span>
    </div>
  );
}

function Checkbox({ label, checked, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`radio-row ${checked ? "selected" : ""}`}
      role="checkbox"
      aria-checked={checked}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 4,
          border: "2px solid #7a1d2e",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: checked ? "#7a1d2e" : "transparent",
        }}
      >
        {checked && <Check size={13} color="#fff" strokeWidth={3} />}
      </div>
      <span>{label}</span>
    </div>
  );
}

// --- Steps ---
function StepPersonal({ data, set }) {
  const toggleCategory = (key) => {
    const has = data.achievement_categories.includes(key);
    const next = has
      ? data.achievement_categories.filter((k) => k !== key)
      : [...data.achievement_categories, key];
    set("achievement_categories", next);
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold" style={{ color: "#111827" }}>
        Заявка на поездку в лагерь — Химический факультет МГУ
      </h1>
      <p className="text-sm" style={{ color: "#4b5563" }}>
        Пожалуйста, заполните все обязательные поля. Обязательные поля отмечены <span className="req">*</span>
      </p>

      <div>
        <Label required>Ваши ФИО</Label>
        <Input testid="form-fio-input" value={data.fio} onChange={(v) => set("fio", v)} placeholder="Иванов Иван Иванович" />
      </div>

      <div>
        <Label required>Вы обучаетесь на бюджетной основе?</Label>
        <Radio
          testid="form-budget-yes"
          label="Да"
          checked={data.is_budget === true}
          onClick={() => set("is_budget", true)}
        />
        <Radio
          testid="form-budget-no"
          label="Нет"
          checked={data.is_budget === false}
          onClick={() => set("is_budget", false)}
        />
      </div>

      <div>
        <Label required>Номер вашего курса</Label>
        <select
          data-testid="form-course-select"
          className="select-input"
          value={data.course}
          onChange={(e) => set("course", e.target.value)}
        >
          <option value="">—</option>
          {COURSES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div>
        <Label required>Номер студенческого/аспирантского билета</Label>
        <Input
          testid="form-student-id-input"
          value={data.student_id}
          onChange={(v) => set("student_id", v)}
          placeholder="00000000"
          inputMode="numeric"
        />
        <div className="field-help">
          Номер состоит из 8 цифр. Если в вашем студенческом указано 7 цифр (или меньше), нужно дописать в начало нужное количество 0.
        </div>
      </div>

      <div>
        <Label required>Номер профсоюзного билета</Label>
        <Input
          testid="form-union-id-input"
          value={data.union_id}
          onChange={(v) => set("union_id", v)}
          placeholder="Номер карты Zachët!"
          inputMode="numeric"
        />
        <div className="field-help">
          Номер карты Zachët! можно узнать в личном кабинете lk.msuprof.com или в сообщениях группы Профкома студентов Химфака.
        </div>
      </div>

      <div>
        <Label required>Ссылка на страницу ВКонтакте</Label>
        <Input testid="form-vk-input" value={data.vk_link} onChange={(v) => set("vk_link", v)} placeholder="https://vk.com/..." />
      </div>

      <div>
        <Label required>Ваш номер телефона</Label>
        <Input testid="form-phone-input" value={data.phone} onChange={(v) => set("phone", v)} placeholder="8XXXXXXXXXX" inputMode="tel" />
      </div>

      <div>
        <Label required>Адрес вашей электронной почты</Label>
        <Input testid="form-email-input" value={data.email} onChange={(v) => set("email", v)} placeholder="you@example.com" type="email" />
      </div>

      <div>
        <Label required>Ссылка на аккаунт в Телеграм</Label>
        <Input testid="form-telegram-input" value={data.telegram_link} onChange={(v) => set("telegram_link", v)} placeholder="https://t.me/..." />
      </div>

      <div>
        <Label>В каких категориях у вас есть достижения?</Label>
        <div className="field-help" style={{ marginTop: 0, marginBottom: 8 }}>
          Отметьте все подходящие варианты — на следующем шаге вы опишете только выбранные категории. Если достижений нет ни в одной из категорий, просто не отмечайте ничего.
        </div>
        {ACHIEVEMENT_CATEGORIES.map((cat) => (
          <Checkbox
            key={cat.key}
            testid={`form-category-${cat.key}`}
            label={cat.label}
            checked={data.achievement_categories.includes(cat.key)}
            onClick={() => toggleCategory(cat.key)}
          />
        ))}
      </div>
    </div>
  );
}

function StepAchievements({ data, set }) {
  const selected = ACHIEVEMENT_CATEGORIES.filter((cat) => data.achievement_categories.includes(cat.key));

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold" style={{ color: "#111827" }}>
        Расскажите о своих достижениях на Химическом факультете!
      </h1>

      {selected.length === 0 ? (
        <p className="text-sm" style={{ color: "#4b5563" }}>
          Вы не отметили ни одной категории достижений на предыдущем шаге. Если это верно — просто нажмите «Далее». Если хотите рассказать о достижениях, вернитесь на шаг назад и отметьте нужные категории.
        </p>
      ) : (
        <p className="text-sm" style={{ color: "#4b5563" }}>
          Опишите достижения по выбранным вами категориям (порядок не имеет значения):
        </p>
      )}

      {selected.map((cat) => (
        <div key={cat.key}>
          <Label required>Ваши достижения: {cat.label}</Label>
          <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }}>
            {cat.help}
          </div>
          <TextArea
            testid={`form-${cat.key}-input`}
            value={data[cat.field]}
            onChange={(v) => set(cat.field, v)}
          />
        </div>
      ))}

      <div>
        <Label required>Ссылка на диск с подтверждениями ваших достижений</Label>
        <Input testid="form-drive-link-input" value={data.drive_link} onChange={(v) => set("drive_link", v)} placeholder="https://disk.yandex.ru/..." />
        <div className="field-help">
          Загрузите, пожалуйста, все документы, подтверждающие ваши достижения, на один диск. Проверьте, что доступ к диску открыт!
        </div>
      </div>

      <div>
        <Label>Сюда можно написать комментарии по поводу ваших достижений</Label>
        <TextArea testid="form-comments-input" value={data.comments} onChange={(v) => set("comments", v)} />
      </div>
    </div>
  );
}

function StepReview({ data, submitting, onSubmit, onBack }) {
  const categoriesLabel = ACHIEVEMENT_CATEGORIES
    .filter((cat) => data.achievement_categories.includes(cat.key))
    .map((cat) => cat.label)
    .join(", ");

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold" style={{ color: "#111827" }}>
        Проверьте вашу заявку
      </h1>
      <p className="text-sm" style={{ color: "#4b5563" }}>
        Перед отправкой убедитесь, что все данные указаны верно. После отправки заявку изменить нельзя.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <ReviewRow label="ФИО" value={data.fio} />
        <ReviewRow label="Бюджет" value={data.is_budget === true ? "Да" : "Нет"} />
        <ReviewRow label="Курс" value={data.course} />
        <ReviewRow label="Студ. билет" value={data.student_id} />
        <ReviewRow label="Профсоюзный билет" value={data.union_id} />
        <ReviewRow label="VK" value={data.vk_link} />
        <ReviewRow label="Телефон" value={data.phone} />
        <ReviewRow label="Email" value={data.email} />
        <ReviewRow label="Telegram" value={data.telegram_link} />
        <ReviewRow label="Категории достижений" value={categoriesLabel} />
        <ReviewRow label="Диск" value={data.drive_link} />
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#7a1d2e" }}>{label}</div>
      <div className="readonly-block" style={{ minHeight: 32, marginTop: 4 }}>{value || "—"}</div>
    </div>
  );
}

function validateStep(step, data) {
  const missing = [];
  if (step === 0) {
    if (!data.fio.trim()) missing.push("ФИО");
    if (data.is_budget !== true && data.is_budget !== false) missing.push("Форма обучения");
    if (!data.course) missing.push("Курс");
    if (!data.student_id.trim()) missing.push("Студенческий билет");
    if (!data.union_id.trim()) missing.push("Профсоюзный билет");
    if (!data.vk_link.trim()) missing.push("VK");
    if (!data.phone.trim()) missing.push("Телефон");
    if (!data.email.trim()) missing.push("Email");
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) missing.push("Email (некорректный формат)");
    if (!data.telegram_link.trim()) missing.push("Telegram");
  }
  if (step === 1) {
    ACHIEVEMENT_CATEGORIES.forEach((cat) => {
      if (data.achievement_categories.includes(cat.key) && !data[cat.field].trim()) {
        missing.push(cat.label);
      }
    });
    if (!data.drive_link.trim()) missing.push("Ссылка на диск");
  }
  return missing;
}

export default function FormPage() {
  const draft = loadDraft();
  const [data, setData] = useState(draft?.data ? { ...INITIAL, ...draft.data } : INITIAL);
  const [step, setStep] = useState(draft?.step ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  // Persist every change so a refresh or reopened tab restores the draft.
  useEffect(() => {
    if (!done) saveDraft(data, step);
  }, [data, step, done]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const goNext = () => {
    const missing = validateStep(step, data);
    if (missing.length) {
      toast.error("Заполните обязательные поля: " + missing.join(", "));
      return;
    }
    setStep((s) => Math.min(s + 1, 2));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...data, comments: data.comments || "" };

      // Categories the user didn't tick get "-" instead of an empty string,
      // so the Google Sheet shows a clear placeholder rather than a blank cell.
      ACHIEVEMENT_CATEGORIES.forEach((cat) => {
        const isSelected = data.achievement_categories.includes(cat.key);
        if (!isSelected || !payload[cat.field].trim()) {
          payload[cat.field] = "-";
        }
      });
      delete payload.achievement_categories;

      const res = await api.post("/applications", payload);
      toast.success(res.data.message || "Заявка отправлена!");
      clearDraft();
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Не удалось отправить заявку. Попробуйте позже.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / 3) * 100;

  return (
    <div className="page-bg">
      <HexBackground />

      <button
        data-testid="admin-login-button"
        className="admin-corner"
        onClick={() => navigate("/admin")}
      >
        <LogIn size={16} /> Вход администратора
      </button>

      <div className="relative z-10 max-w-2xl mx-auto py-8 sm:py-12 px-4">
        {!done ? (
          <div className="form-card">
            <div className="px-6 sm:px-10 pt-6">
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-xs" style={{ color: "#6b7280" }}>Шаг {step + 1} из 3</div>
                <img src="/profkom-logo.png?v=2" alt="Логотип профкома" className="form-logo" />
              </div>
            </div>

            <div className="p-6 sm:p-10">
              {step === 0 && <StepPersonal data={data} set={set} />}
              {step === 1 && <StepAchievements data={data} set={set} />}
              {step === 2 && <StepReview data={data} />}

              <div className="mt-8 flex items-center justify-between gap-3">
                {step > 0 ? (
                  <button data-testid="form-prev-step-button" className="btn-secondary" onClick={goBack}>
                    <ArrowLeft size={16} style={{ display: "inline", marginRight: 4 }} /> Назад
                  </button>
                ) : (
                  <div />
                )}
                {step < 2 ? (
                  <button data-testid="form-next-step-button" className="btn-primary" onClick={goNext}>
                    Далее <ArrowRight size={16} style={{ display: "inline", marginLeft: 4 }} />
                  </button>
                ) : (
                  <button
                    data-testid="form-submit-button"
                    className="btn-primary"
                    onClick={submit}
                    disabled={submitting}
                  >
                    <Send size={16} style={{ display: "inline", marginRight: 6 }} />
                    {submitting ? "Отправка..." : "Отправить заявку"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="form-card p-8 sm:p-12 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 size={64} className="text-burgundy" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Заявка отправлена!</h1>
            <p className="text-sm mb-6" style={{ color: "#4b5563" }}>
              Спасибо за заполнение анкеты. Мы свяжемся с вами по указанным контактам.
            </p>
            <button
              data-testid="form-new-application-button"
              className="btn-primary"
              onClick={() => { clearDraft(); setData(INITIAL); setStep(0); setDone(false); }}
            >
              Заполнить ещё одну заявку
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
